/**
 * Individual module page.
 * Generic container for module-specific UI. Shows module status and mounts
 * module-specific components when the module is active.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchModule } from '../utils/api';

interface Module {
  id: string;
  slug: string;
  name: string;
  version: string;
  status: string;
  config: Record<string, unknown> | null;
}

export default function ModulePage() {
  const { slug } = useParams<{ slug: string }>();
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadModule() {
      if (!slug) return;

      try {
        const data = await fetchModule(slug);
        setModule(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load module');
      } finally {
        setLoading(false);
      }
    }

    loadModule();
  }, [slug]);

  if (loading) {
    return (
      <div className="page-container">
        <p className="loading-text">Loading module...</p>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="page-container">
        <p className="error-text">{error || 'Module not found'}</p>
        <Link to="/modules" className="button button-secondary">
          ← Back to Modules
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="breadcrumb">
          <Link to="/modules" className="breadcrumb-link">
            Modules
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{module.name}</span>
        </div>
        <h1 className="page-title">{module.name}</h1>
        <div className="page-meta">
          <span className={`module-status status-${module.status}`}>
            {module.status}
          </span>
          <span className="module-version">v{module.version}</span>
        </div>
      </header>

      {module.status === 'disabled' || module.status === 'archived' ? (
        <div className="card">
          <h2 className="card-title">Module Not Active</h2>
          <p className="card-text">
            This module isn't active yet. Enable it in Settings to start using it.
          </p>
        </div>
      ) : (
        <div className="card">
          <h2 className="card-title">Module Active</h2>
          <p className="card-text">
            This module is ready to use. Module-specific UI will appear here once implemented.
          </p>
        </div>
      )}
    </div>
  );
}
